import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Copy, CheckCircle2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PersistentRecoveryReveal — fires after a successful login via admin
 * bypass when `pending_recovery_reset_after_bypass` is set. Calls the
 * server to mint a fresh recovery key, reveals it once, and forces
 * the user to acknowledge within 2 minutes.
 *
 * Re-uses the same session-flag/timer pattern as the other persistent
 * modals: refresh → force-logout, timer expiry → force-logout.
 */
const SESSION_FLAG = '__orb_recovery_reveal_in_progress';
// iter163 — 5-minute (300 s) reveal window per the security spec. The
// admin's pending_recovery bypass timer is reset to "now" the moment
// the user finishes the MFA finalize step (see server.py
// me_finalize_mfa_after_bypass) so the recovery clock starts fresh
// when the user actually arrives at this screen, not when they logged in.
const TTL_MS = 300 * 1000;

export default function PersistentRecoveryReveal() {
  const { user, refreshUser, api, logout } = useAuth();
  // Defer until any pending MFA-after-bypass reset is finished —
  // when both factors are bypassed the spec is MFA first, then
  // recovery key. PersistentMfaSetup handles the MFA half.
  const mfaStillPending = !!user?.pending_mfa_reset_after_bypass;
  const required = !mfaStillPending && user?.pending_recovery_reset_after_bypass;
  const [revealedKey, setRevealedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [acked, setAcked] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!required) return;
    const flag = sessionStorage.getItem(SESSION_FLAG);
    if (flag) { sessionStorage.removeItem(SESSION_FLAG); toast.error('Session ended'); logout(); return; }
    sessionStorage.setItem(SESSION_FLAG, String(Date.now()));
    setStartedAt(Date.now());
    // Auto-fetch the fresh key on mount.
    api.post('/me/security/finalize-recovery-after-bypass').then((r) => {
      setRevealedKey(r.data?.recovery_key);
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || 'Could not generate key');
      sessionStorage.removeItem(SESSION_FLAG);
      logout();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [required]);

  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [startedAt]);

  useEffect(() => {
    if (!startedAt || acked) return;
    if (now - startedAt >= TTL_MS) {
      sessionStorage.removeItem(SESSION_FLAG);
      toast.error('Time expired — bypass required again');
      logout();
    }
  }, [now, startedAt, logout, acked]);

  useEffect(() => {
    if (!required) return;
    const block = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); } };
    window.addEventListener('keydown', block, true);
    return () => window.removeEventListener('keydown', block, true);
  }, [required]);

  if (!user || !required) return null;

  const remaining = startedAt ? Math.max(0, Math.ceil((TTL_MS - (now - startedAt)) / 1000)) : 300;
  const mm = String(Math.floor(remaining / 60)).padStart(1, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/85 p-4"
      style={{ backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}
      data-testid="persistent-recovery-reveal-overlay"
    >
      <div className="w-full max-w-md rounded-2xl bg-background border border-amber-500/40 shadow-2xl p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-100 dark:bg-amber-900/40 p-2">
            <KeyRound className="w-5 h-5 text-amber-600 dark:text-amber-300" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-base">Save Your Recovery Key</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Admin bypass — save within window. Single-use. We can't show it again.</p>
          </div>
          <span className="text-[11px] font-mono font-bold text-red-600 dark:text-red-300" data-testid="recovery-reveal-timer">{mm}:{ss}</span>
        </div>

        {revealedKey ? (
          <>
            <div className="rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 dark:bg-amber-900/20 p-4 text-center space-y-2">
              <p className="font-mono font-bold text-xl tracking-widest break-all" data-testid="recovery-reveal-value">{revealedKey}</p>
              <button
                onClick={() => { navigator.clipboard.writeText(revealedKey); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                className="text-[11px] text-amber-700 dark:text-amber-300 inline-flex items-center gap-1 underline"
                data-testid="recovery-reveal-copy"
              >
                {copied ? <><CheckCircle2 className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} className="mt-0.5" data-testid="recovery-reveal-ack" />
              <span>I've saved this key.</span>
            </label>
            <Button
              className="w-full bg-[#586D52] hover:bg-[#475a43]"
              onClick={async () => { sessionStorage.removeItem(SESSION_FLAG); await refreshUser(); }}
              disabled={!acked}
              data-testid="recovery-reveal-continue"
            >
              Continue
            </Button>
          </>
        ) : (
          <p className="text-xs text-center text-muted-foreground py-4">Generating…</p>
        )}
      </div>
    </div>
  );
}
